/**
 * Field Agent Daily Log Entry — Phase 2A (2026-04-23)
 * /portal/field-agent/log-entry
 *
 * Mobile-first: sticky save bar, one-column, large tap targets, localStorage draft autosave.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, ArrowLeft, Save, Send, MapPin, AlertTriangle, Camera, X as XIcon, Calendar as CalendarIcon } from "lucide-react";
import AgentRenewalBanner from "../../components/fieldAgent/AgentRenewalBanner";
import AgentDisputePanel from "../../components/fieldAgent/AgentDisputePanel";


const EV_TYPES = [["storefront_photo","Storefront"],["signage_photo","Signage"],["operator_photo","Operator"],["document","Document"],["other","Other"]];
const EV_MAX_COUNT = 5;
const EV_MAX_BYTES = 3 * 1024 * 1024;

// Client-side downscale + compress to JPEG under EV_MAX_BYTES (best-effort)
async function compressImage(file, maxWidth = 1600, initialQuality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const tryQuality = (q) => canvas.toBlob((b) => {
        if (!b) return reject(new Error("compression failed"));
        if (b.size > EV_MAX_BYTES && q > 0.4) return tryQuality(q - 0.1);
        resolve(new File([b], (file.name || "evidence.jpg").replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", q);
      tryQuality(initialQuality);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const JM_PARISHES = [
  "Kingston", "St. Andrew", "Kingston and St. Andrew", "St. Catherine", "Clarendon",
  "Manchester", "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas",
];
const CHANNELS = [["walk_in","Walk-in"],["phone_call","Phone call"],["referral","Referral"],["scheduled_meeting","Scheduled meeting"]];
const INTENTS = [["low","Low"],["medium","Medium"],["high","High"]];
const BIZ_TYPES = [["pharmacy","Pharmacy"],["supermarket","Supermarket"],["taxi","Taxi"],["individual","Individual"],["other","Other"]];
const PROSPECT_TYPES = [["pudo","PUDO centre"],["driver","Driver"],["seller_clockinja","Seller / ClockInJA"],["other","Other"]];
const OUTCOMES = [
  ["new","New"],["contacted","Contacted"],["interested","Interested"],["not_interested","Not interested"],
  ["follow_up_needed","Follow-up needed"],["application_started","Application started"],["submitted","Submitted"],
  ["approved","Approved"],["activated","Activated"],["first_transaction_complete","First transaction complete"],
];

function todayJM() {
  // Jamaica has no DST; UTC-05:00
  const d = new Date(Date.now() - 5 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}
function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function FieldAgentLogEntry() {
  const navigate = useNavigate();
  const DRAFT_KEY = "fa_activity_draft_v1";
  const NONCE_KEY = "fa_activity_nonce_v1";

  const initialNonce = (() => {
    let n = window.localStorage.getItem(NONCE_KEY);
    if (!n) { n = uuidv4(); window.localStorage.setItem(NONCE_KEY, n); }
    return n;
  })();

  const [form, setForm] = useState({
    activity_date: todayJM(),
    area: "", parish: "Kingston and St. Andrew",
    time_started: "", time_ended: "",
    visit_channel: "walk_in",
    prospect: { type: "pudo", name: "", contact_person: "", phone: "", email: "", address: "", business_type: "pharmacy" },
    visit_outcome: "contacted",
    conversion_intent: "medium",
    notes: "", next_action: "", follow_up_date: "",
  });
  const [entrySource, setEntrySource] = useState("manual");
  const [submitting, setSubmitting] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(true);
  const [dup, setDup] = useState(null);
  const [dupAck, setDupAck] = useState({ open: false, reason: "" }); // Phase 2C friction modal
  const [evidenceBuf, setEvidenceBuf] = useState([]); // [{id, file, type, preview, token}]
  const dirtyRef = useRef({}); // field-name -> true if user typed

  // ── Prefill + draft restore on mount
  useEffect(() => {
    (async () => {
      try {
        const [prefRes, draftRaw] = await Promise.all([
          api.get("/field-activities/session-prefill").catch(() => null),
          Promise.resolve(window.localStorage.getItem(DRAFT_KEY)),
        ]);
        let base = { ...form };
        let prefilled = false;

        // 1) Draft restore wins over prefill
        if (draftRaw) {
          try {
            const draft = JSON.parse(draftRaw);
            base = { ...base, ...draft, activity_date: todayJM() }; // always use today
            toast.info("Draft restored");
          } catch { /* ignore */ }
        } else if (prefRes?.data && prefRes.data.expired === false) {
          if (prefRes.data.last_area) { base.area = prefRes.data.last_area; prefilled = true; }
          if (prefRes.data.last_parish) { base.parish = prefRes.data.last_parish; prefilled = true; }
          if (prefRes.data.last_prospect_type) { base.prospect = { ...base.prospect, type: prefRes.data.last_prospect_type }; prefilled = true; }
        }
        setForm(base);
        setEntrySource(prefilled ? "prefilled" : "manual");
      } finally {
        setLoadingPrefill(false);
      }
    })();
  }, []);

  // ── Draft autosave every 15s
  useEffect(() => {
    const t = setInterval(() => {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    }, 15000);
    return () => clearInterval(t);
  }, [form]);

  const setField = (k, v) => { dirtyRef.current[k] = true; setForm((f) => ({ ...f, [k]: v })); };
  const setProspect = (k, v) => { dirtyRef.current[`prospect.${k}`] = true; setForm((f) => ({ ...f, prospect: { ...f.prospect, [k]: v } })); };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS not available");
    navigator.geolocation.getCurrentPosition(
      (pos) => setField("geo", { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
      () => toast.error("GPS denied or unavailable"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (evidenceBuf.length + files.length > EV_MAX_COUNT) {
      toast.error(`Max ${EV_MAX_COUNT} photos per visit`);
      return;
    }
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error("Images only"); continue; }
      try {
        const compressed = await compressImage(f);
        if (compressed.size > EV_MAX_BYTES) { toast.error(`${f.name}: too large after compression`); continue; }
        const preview = URL.createObjectURL(compressed);
        setEvidenceBuf((prev) => [...prev, { id: uuidv4().slice(0, 8), file: compressed, type: "storefront_photo", preview, token: uuidv4() }]);
      } catch {
        toast.error(`Could not process ${f.name}`);
      }
    }
  };

  const removePhoto = (id) => {
    setEvidenceBuf((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const setPhotoType = (id, type) => setEvidenceBuf((prev) => prev.map((p) => (p.id === id ? { ...p, type } : p)));

  const uploadEvidence = async (activityId) => {
    if (!evidenceBuf.length) return { ok: 0, fail: 0 };
    let ok = 0, fail = 0;
    for (const item of evidenceBuf) {
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("upload_token", item.token);
      fd.append("evidence_type", item.type);
      try {
        await api.post(`/field-activities/${activityId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    return { ok, fail };
  };

  const saveDraftLocal = () => {
    // Phase 2A: drafts live in localStorage only (offline resilience).
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    toast.success("Draft saved on this device");
  };

  const submit = async (extraAck) => {
    if (submitting) return;
    if (!form.prospect.name.trim() && !form.prospect.phone.trim()) {
      toast.error("At least one of prospect name or phone is required"); return;
    }
    if (form.time_started && form.time_ended && form.time_started > form.time_ended) {
      toast.error("Start time must be before end time"); return;
    }
    setSubmitting(true);
    setDup(null);
    const payload = {
      ...form,
      prospect: { ...form.prospect },
      activity_nonce: initialNonce,
      entry_source: (entrySource === "prefilled" || Object.keys(dirtyRef.current).length === 0) ? entrySource : "manual",
      submit: true,
    };
    if (extraAck) payload.duplicate_ack = { ack: true, reason: extraAck };
    // strip empty strings → null where backend expects null
    ["time_started","time_ended","follow_up_date","notes","next_action"].forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    if (payload.prospect.email === "") payload.prospect.email = null;
    if (payload.prospect.contact_person === "") payload.prospect.contact_person = null;
    if (payload.prospect.address === "") payload.prospect.address = null;
    try {
      const res = await api.post("/field-activities", payload);
      const warn = res.data.duplicate_warning;
      const newActivityId = res.data.activity?.id;
      if (warn) setDup(warn);
      // Upload buffered evidence if any
      let evStatus = null;
      if (newActivityId && evidenceBuf.length > 0) {
        toast.info(`Uploading ${evidenceBuf.length} photo(s)…`);
        evStatus = await uploadEvidence(newActivityId);
      }
      window.localStorage.removeItem(DRAFT_KEY);
      window.localStorage.removeItem(NONCE_KEY);
      // Clean up blob URLs
      evidenceBuf.forEach((e) => { if (e.preview) URL.revokeObjectURL(e.preview); });
      if (evStatus && evStatus.fail > 0) {
        toast.warning(`Activity saved. ${evStatus.ok}/${evStatus.ok + evStatus.fail} photos uploaded. Retry from Weekly Rollup.`);
      } else {
        toast.success(warn?.friction_required ? "Submitted (flagged as possible duplicate)" : "Activity submitted");
      }
      navigate("/portal/field-agent/weekly-rollup");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code || e?.response?.data?.error?.code;
      if (code === "DUPLICATE_SUBMIT") {
        const existing = detail.existing_id;
        toast.info(`Already saved (${existing})`);
        window.localStorage.removeItem(DRAFT_KEY);
        window.localStorage.removeItem(NONCE_KEY);
      } else if (code === "DUPLICATE_FRICTION_REQUIRED") {
        setDup(detail.duplicate_signal);
        setDupAck({ open: true, reason: "" });
      } else if (code === "TERRITORY_EXCLUSIVE_VIOLATION") {
        toast.error(detail.message || "Parish is outside your exclusive territory");
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Save failed");
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPrefill) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="field-agent-log-entry-page"><div className="max-w-3xl mx-auto px-4 pt-3"><AgentRenewalBanner /></div>
      <div className="max-w-xl mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Log activity</h1>
            <p className="text-xs text-slate-500" data-testid="entry-source-prefilled">{entrySource === "prefilled" ? "Prefilled from your last visit" : "New entry"}</p>
          </div>
        </div>

        {/* Section 1 — basics */}
        <section className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
          <h2 className="text-sm font-semibold mb-3 text-slate-700">Visit basics</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2"><label className="text-xs text-slate-500">Date</label><Input type="date" value={form.activity_date} onChange={(e) => setField("activity_date", e.target.value)} data-testid="fa-date" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Area / neighbourhood</label><Input value={form.area} onChange={(e) => setField("area", e.target.value)} placeholder="e.g. Half Way Tree" data-testid="fa-area" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Parish</label>
              <Select value={form.parish} onValueChange={(v) => setField("parish", v)}>
                <SelectTrigger data-testid="fa-parish"><SelectValue /></SelectTrigger>
                <SelectContent>{JM_PARISHES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Start</label><Input type="time" value={form.time_started} onChange={(e) => setField("time_started", e.target.value)} data-testid="fa-ts" /></div>
            <div><label className="text-xs text-slate-500">End</label><Input type="time" value={form.time_ended} onChange={(e) => setField("time_ended", e.target.value)} data-testid="fa-te" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Channel</label>
              <Select value={form.visit_channel} onValueChange={(v) => setField("visit_channel", v)}>
                <SelectTrigger data-testid="fa-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Button type="button" variant="outline" size="sm" onClick={useMyLocation} className="w-full" data-testid="fa-gps">
                <MapPin className="w-3 h-3 mr-1" /> {form.geo ? `GPS: ${form.geo.lat.toFixed(4)}, ${form.geo.lng.toFixed(4)}` : "Use my GPS"}
              </Button>
            </div>
          </div>
        </section>

        {/* Section 2 — prospect */}
        <section className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
          <h2 className="text-sm font-semibold mb-3 text-slate-700">Prospect</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><label className="text-xs text-slate-500">Type</label>
              <Select value={form.prospect.type} onValueChange={(v) => setProspect("type", v)}>
                <SelectTrigger data-testid="fa-ptype"><SelectValue /></SelectTrigger>
                <SelectContent>{PROSPECT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Business type</label>
              <Select value={form.prospect.business_type} onValueChange={(v) => setProspect("business_type", v)}>
                <SelectTrigger data-testid="fa-btype"><SelectValue /></SelectTrigger>
                <SelectContent>{BIZ_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Name *</label><Input value={form.prospect.name} onChange={(e) => setProspect("name", e.target.value)} placeholder="Business or person name" data-testid="fa-name" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Contact person</label><Input value={form.prospect.contact_person} onChange={(e) => setProspect("contact_person", e.target.value)} data-testid="fa-contact" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Phone *</label><Input inputMode="tel" value={form.prospect.phone} onChange={(e) => setProspect("phone", e.target.value)} placeholder="876-555-1234" data-testid="fa-phone" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Email</label><Input inputMode="email" value={form.prospect.email} onChange={(e) => setProspect("email", e.target.value)} data-testid="fa-email" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Address</label><Input value={form.prospect.address} onChange={(e) => setProspect("address", e.target.value)} data-testid="fa-address" /></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">* at least one of Name or Phone is required</p>
        </section>

        {/* Section 2b — evidence (Phase 2B) */}
        <section className="bg-white rounded-xl border border-slate-200 p-4 mb-3" data-testid="fa-evidence-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Photos <span className="text-slate-400 font-normal">({evidenceBuf.length}/{EV_MAX_COUNT})</span></h2>
            <label className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-300 bg-emerald-50 rounded px-2 py-1 cursor-pointer">
              <Camera className="w-3 h-3" /> Add photo
              <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} data-testid="fa-photo-add" />
            </label>
          </div>
          {evidenceBuf.length === 0 ? (
            <p className="text-xs text-slate-400">Attach up to {EV_MAX_COUNT} photos: storefront, signage, operator, document, or other. Max 3 MB each (auto-compressed).</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {evidenceBuf.map((p) => (
                <div key={p.id} className="relative border border-slate-200 rounded-lg overflow-hidden" data-testid={`fa-photo-item-${p.id}`}>
                  <img src={p.preview} alt="evidence" className="w-full h-28 object-cover" />
                  <button type="button" onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 shadow" data-testid={`fa-photo-remove-${p.id}`}>
                    <XIcon className="w-3 h-3 text-rose-600" />
                  </button>
                  <select value={p.type} onChange={(e) => setPhotoType(p.id, e.target.value)} className="w-full text-xs border-t border-slate-200 px-2 py-1 bg-white" data-testid={`fa-photo-type-${p.id}`}>
                    {EV_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section 3 — outcome */}
        <section className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
          <h2 className="text-sm font-semibold mb-3 text-slate-700">Outcome</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><label className="text-xs text-slate-500">Visit outcome</label>
              <Select value={form.visit_outcome} onValueChange={(v) => setField("visit_outcome", v)}>
                <SelectTrigger data-testid="fa-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{OUTCOMES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Conversion intent</label>
              <Select value={form.conversion_intent} onValueChange={(v) => setField("conversion_intent", v)}>
                <SelectTrigger data-testid="fa-intent"><SelectValue /></SelectTrigger>
                <SelectContent>{INTENTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Notes</label><textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} maxLength={1000} className="w-full border border-slate-300 rounded px-3 py-2 text-sm min-h-[80px]" data-testid="fa-notes" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Next action</label><Input value={form.next_action} onChange={(e) => setField("next_action", e.target.value)} maxLength={200} data-testid="fa-nextaction" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Follow-up date</label><Input type="date" value={form.follow_up_date} onChange={(e) => setField("follow_up_date", e.target.value)} data-testid="fa-followup" /></div>
          </div>
        </section>

        {dup && (
          <div className={`rounded-xl border p-3 mb-3 text-sm ${dup.friction_required ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid="fa-dup-warn">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle className="w-4 h-4" /> Possible duplicate (score {dup.score})
            </div>
            <div className="text-xs mt-1">Primary reason: <b>{dup.primary_reason}</b> · also: {dup.contributing_reasons.join(", ")}</div>
          </div>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 flex gap-2 max-w-xl mx-auto">
        <Button variant="outline" className="flex-1" disabled={submitting} onClick={saveDraftLocal} data-testid="fa-save-draft"><Save className="w-4 h-4 mr-1" /> Save draft</Button>
        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={submitting} onClick={() => submit()} data-testid="fa-submit">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Submit</>}</Button>
      </div>

      {dupAck.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="fa-dup-friction-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-5">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold">Possible duplicate detected</h3>
                <p className="text-xs text-slate-600 mt-1">Score {dup?.score?.toFixed(2)} · primary: {dup?.primary_reason}. Please explain why you're submitting this record anyway (minimum 20 characters).</p>
              </div>
            </div>
            <textarea
              value={dupAck.reason}
              onChange={(e) => setDupAck({ ...dupAck, reason: e.target.value })}
              placeholder="e.g., Same phone but operator said they never signed up — revisited to confirm"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[100px]"
              data-testid="fa-dup-reason"
            />
            <div className="text-[11px] text-slate-400 mb-3">{dupAck.reason.length}/20 min</div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setDupAck({ open: false, reason: "" })} data-testid="fa-dup-cancel">Cancel</Button>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700"
                disabled={dupAck.reason.trim().length < 20 || submitting}
                onClick={() => { const r = dupAck.reason.trim(); setDupAck({ open: false, reason: "" }); submit(r); }}
                data-testid="fa-dup-confirm"
              >
                Submit anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
